import {
  ApolloCache,
  FetchResult,
  FieldMergeFunction,
  FieldPolicy,
  isReference,
} from '@apollo/client';

import type { ModifierDetails } from '@apollo/client/cache';

export type ItemInCollection = {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  __ref: string;
};

type ItemConnection = {
  items: ItemInCollection[];
  nextToken: string | null;
};

const merge: FieldMergeFunction<ItemConnection, ItemConnection> = (
  existing,
  incoming,
) => {
  return {
    ...(existing ?? []),
    ...incoming,
    items: [
      ...(existing?.items ?? []).filter(item =>
        incoming.items.every(i => i.__ref !== item.__ref),
      ),
      ...incoming.items,
    ],
  };
};

export const itemConnectionFieldPolicy: FieldPolicy<
  ItemConnection,
  ItemConnection
> = {
  keyArgs: ['__type'],
  merge,
};

export const modifyCacheOnDeleteItemInItemConnection = <
  TData,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  TCache extends ApolloCache<any>,
>(
  fieldName: string,
  deletedItemId: string | undefined,
  cache: TCache,
  result: Omit<FetchResult<TData>, 'context'>,
) => {
  if (!result.data || !deletedItemId) return;

  function isItemConnectionFieldValue(
    v: unknown,
  ): v is { items?: ItemInCollection[] } {
    return typeof v === 'object' && v !== null && !isReference(v);
  }

  const modifier = (
    value: unknown,
    _details: ModifierDetails,
  ): { items: ItemInCollection[] } | undefined => {
    if (!isItemConnectionFieldValue(value)) return undefined;

    return {
      ...value,
      items: (value.items ?? []).filter(
        (item: ItemInCollection) => !item.__ref.includes(deletedItemId),
      ),
    };
  };

  cache.modify({
    fields: {
      [fieldName]: modifier,
    },
  });
};
